import React, { useEffect, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';

import SectionCard from './SectionCard';
import { Fact, FactGrid } from './sectionParts';
import dateTimeUtils from '../../../utils/dateTimeUtils';
import { useTimezone } from '../../../hooks/useTimezone';
import { AdmissionCycle } from '../../../types/admissions';
import RollingAdmissionsSwitch from './RollingAdmissionsSwitch';

interface Props {
  cycle: AdmissionCycle;
  canEdit: boolean;
  onSave: (patch: Partial<AdmissionCycle>) => Promise<void>;
}

type Dates = { applicationOpen: string; applicationClose: string; admissionFeeDeadline: string; classesBeginOn: string; rolling: boolean };

const day = (iso?: string | null) => (iso ? String(iso).slice(0, 10) : '');
const fromCycle = (c: AdmissionCycle): Dates => ({
  applicationOpen: day(c.schedule?.applicationOpen),
  applicationClose: day(c.schedule?.applicationClose),
  admissionFeeDeadline: day(c.schedule?.admissionFeeDeadline),
  classesBeginOn: day(c.schedule?.classesBeginOn),
  rolling: !c.schedule?.applicationClose,
});

/**
 * The cycle's key dates. Once the cycle is open the open date is fixed
 * (applications have started); the close date can still move to today or
 * later — extend a slow cycle, or close one early. With no closing date
 * (rolling admissions) the online form stays open until it is paused.
 */
const CycleDatesSection: React.FC<Props> = ({ cycle, canEdit, onSave }) => {
  const { getToday } = useTimezone();
  const isOpen = cycle.status === 'Open';
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Dates>(() => fromCycle(cycle));

  useEffect(() => { if (editing) setDraft(fromCycle(cycle)); }, [editing, cycle]);

  const saved = fromCycle(cycle);
  const today = getToday();
  const closeMoved = draft.applicationClose !== day(cycle.schedule?.applicationClose);
  const errors: Partial<Record<keyof Dates, string>> = {};
  if (!draft.applicationOpen) errors.applicationOpen = 'Required';
  if (draft.rolling) { /* no close date to check */ } else if (!draft.applicationClose) errors.applicationClose = 'Required';
  else if (draft.applicationOpen && draft.applicationClose <= draft.applicationOpen) errors.applicationClose = 'Must be after the open date';
  else if (isOpen && closeMoved && draft.applicationClose < today) errors.applicationClose = 'Choose today or a later date';
  if (!draft.admissionFeeDeadline) errors.admissionFeeDeadline = 'Required';
  if (!draft.classesBeginOn) errors.classesBeginOn = 'Required';
  else if (!draft.rolling && draft.applicationClose && draft.classesBeginOn < draft.applicationClose) errors.classesBeginOn = 'On or after the close date';
  const valid = Object.keys(errors).length === 0;

  const save = async () => {
    setSaving(true);
    try {
      const { rolling, ...dates } = draft;
      await onSave({ schedule: { ...cycle.schedule, ...dates, applicationClose: rolling ? null : dates.applicationClose } });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const fmt = (iso: string) => (iso ? dateTimeUtils.formatDateForDisplay(iso) : '—');
  const input = (key: Exclude<keyof Dates, 'rolling'>, label: string, help?: string, extra: { disabled?: boolean; min?: string } = {}) => (
    <TextField
      size="small"
      type="date"
      label={label}
      value={draft[key]}
      onChange={(e) => setDraft(d => ({ ...d, [key]: e.target.value }))}
      error={!!errors[key]}
      helperText={errors[key] || help || ' '}
      disabled={extra.disabled}
      InputLabelProps={{ shrink: true }}
      inputProps={{ min: extra.min }}
      fullWidth
    />
  );

  return (
    <SectionCard
      title="Key dates"
      description="When applications open and close, when the admission fee is due, and when classes begin."
      canEdit={canEdit}
      editing={editing}
      onEditToggle={setEditing}
      onSave={save}
      onCancel={() => setDraft(fromCycle(cycle))}
      saving={saving}
      canSave={valid}
    >
      {editing ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          {input('applicationOpen', 'Applications open', isOpen ? 'Applications have opened, so this date is fixed.' : undefined, { disabled: isOpen })}
          {draft.rolling
            ? <Box sx={{ display: 'flex', alignItems: 'center', pb: 2.5 }}><Typography variant="body2" color="text.secondary">No closing date</Typography></Box>
            : input('applicationClose', 'Applications close', isOpen ? 'Extend it, or close early from today.' : undefined, { min: isOpen ? today : undefined })}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <RollingAdmissionsSwitch checked={draft.rolling} onChange={rolling => setDraft(d => ({ ...d, rolling, applicationClose: rolling ? '' : d.applicationClose || today }))} />
          </Box>
          {input('admissionFeeDeadline', 'Admission fee due', 'Challans issued from now on fall due on this date.')}
          {input('classesBeginOn', 'Classes begin')}
        </Box>
      ) : (
        <FactGrid columns={4}>
          <Fact label="Applications open">{fmt(saved.applicationOpen)}</Fact>
          <Fact label="Applications close">{saved.rolling ? 'No closing date' : fmt(saved.applicationClose)}</Fact>
          <Fact label="Admission fee due">{fmt(saved.admissionFeeDeadline)}</Fact>
          <Fact label="Classes begin">{fmt(saved.classesBeginOn)}</Fact>
        </FactGrid>
      )}
    </SectionCard>
  );
};

export default CycleDatesSection;
